import React, { useState, useEffect, useCallback } from "react";
import Dropdown from "../../components/Dropdown";
import IconCaretDown from "../../components/Icon/IconCaretDown";
import eventService from "../../service/event.service";
import { DataTable } from "mantine-datatable";
import CustomPagination from "../../components/Layouts/CustomPagination";

const WinnerUser = () => {
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Pagination and Winners state
    const [winners, setWinners] = useState([]);
    const [winnersLoading, setWinnersLoading] = useState(false);
    const [csvLoading, setCsvLoading] = useState(false);
    const [page, setPage] = useState(0); // 0-indexed for CustomPagination
    const PAGE_SIZES = [20];
    const [pageSize] = useState(20);
    const [totalRecords, setTotalRecords] = useState(0);

    const fetchEvents = async () => {
        try {
            const response = await eventService.getAllEvents();
            const eventsData = response?.data?.data || [];
            setEvents(eventsData);
            if (eventsData.length > 0) {
                setSelectedEvent(eventsData[0]);
            }
        } catch (error) {
            console.error("Error fetching events:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchWinners = useCallback(async () => {
        if (!selectedEvent) return;
        
        const eventId = selectedEvent.event_id || selectedEvent.event_ids?.[0];
        
        try {
            setWinnersLoading(true);
            // Send page + 1 if API is 1-indexed
            const response = await eventService.getWinnerList(page + 1, pageSize, eventId);
            const result = response?.data;
            setWinners(result?.data || []);
            setTotalRecords(result?.pagination?.totalItems || 0);
        } catch (error) {
            console.error("Error fetching winners:", error);
            setWinners([]);
            setTotalRecords(0);
        } finally {
            setWinnersLoading(false);
        }
    }, [selectedEvent, page, pageSize]);

    useEffect(() => {
        fetchEvents();
    }, []);

    useEffect(() => {
        if (selectedEvent) {
            fetchWinners();
        }
    }, [selectedEvent, page, pageSize, fetchWinners]);

    const eventOptions = events.map((event) => ({
        value: event.event_id || event.event_ids?.[0],
        label: `${event.event_title}`,
        event: event
    }));

    const handleEventChange = (option) => {
        setSelectedEvent(option.event);
        setPage(0); // Reset to first page
    };

    const handlePaginationChange = (type, newPage) => {
        const totalPages = Math.ceil(totalRecords / pageSize);
        if (type === "Next") {
            if (page < totalPages - 1) setPage(page + 1);
        } else if (type === "Previous") {
            if (page > 0) setPage(page - 1);
        } else if (type === "First") {
            setPage(0);
        } else if (type === "Last") {
            setPage(totalPages - 1);
        } else {
            setPage(newPage);
        }
    };

    const downloadCSV = async () => {
        if (!selectedEvent) return;
        
        const eventId = selectedEvent.event_id || selectedEvent.event_ids?.[0];
        
        try {
            setCsvLoading(true);
            const response = await eventService.getAllWinnersForCSV(eventId);
            const allWinners = response?.data?.data || [];
            
            if (allWinners.length === 0) {
                alert('No winners data available for this event');
                return;
            }
            
            // Create CSV content
            const headers = ['Rank', 'User ID', 'User Name', 'Email', 'Points'];
            const csvContent = [
                headers.join(','),
                ...allWinners.map(winner => [
                    winner.rank || '',
                    winner.userId || '',
                    `"${winner.userName || ''}"`, // Wrap in quotes to handle commas
                    `"${winner.email || ''}"`,
                    winner.points || 0
                ].join(','))
            ].join('\n');
            
            // Create blob and download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `winners_${selectedEvent.event_title.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading CSV:', error);
            alert('Failed to download CSV. Please try again.');
        } finally {
            setCsvLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Winner User</h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {selectedEvent ? `Selected Event: ${selectedEvent.event_title}` : "Select an event"}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {selectedEvent && (
                        <button
                            onClick={downloadCSV}
                            disabled={csvLoading}
                            className="btn btn-outline-primary"
                        >
                            {csvLoading ? (
                                <>
                                    <span className="animate-spin mr-2">⏳</span>
                                    Downloading...
                                </>
                            ) : (
                                <>
                                    <span className="mr-2">📥</span>
                                    Download CSV
                                </>
                            )}
                        </button>
                    )}
                    <div className="dropdown">
                        <Dropdown
                            btnClassName="btn btn-outline-primary dropdown-toggle shadow-none"
                            button={
                                <>
                                    {selectedEvent ? `${selectedEvent.event_title} ` : "Select Event"}
                                    <span>
                                        <IconCaretDown className="ltr:ml-1 rtl:mr-1 inline-block" />
                                    </span>
                                </>
                            }
                            options={eventOptions}
                            onOptionSelect={handleEventChange}
                        />
                    </div>
                </div>
            </div>
            
            <div className="panel">
                <div className="flex items-center justify-between mb-5">
                    <h5 className="font-semibold text-lg dark:text-white-light">
                        {loading ? "Loading events..." : selectedEvent ? `Winners List for ${selectedEvent.event_title}` : "No event selected"}
                    </h5>
                </div>

                <div className="datatables">
                    <DataTable
                        highlightOnHover
                        className="whitespace-nowrap table-hover"
                        records={winners}
                        columns={[
                            { accessor: "rank", title: "Rank", width: 80 },
                            { accessor: "userId", title: "User ID", width: 100 },
                            { accessor: "userName", title: "User Name" },
                            { accessor: "email", title: "Email" },
                            { accessor: "points", title: "Points", textAlignment: "right" },
                        ]}
                        minHeight={200}
                        loading={winnersLoading}
                        noRecordsText="No winners found for this event"
                    />
                    
                    {winners.length > 0 && (
                        <div className="mt-5">
                            <CustomPagination
                                total={totalRecords}
                                page={page}
                                handleChange={handlePaginationChange}
                                dataPerPage={pageSize}
                                handleDataPerPage={() => {}} // Disabled as requested
                                PAGE_SIZES={PAGE_SIZES}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WinnerUser;
